import { useConfirmation } from '@components/modal';
import { useDeleteMcpServerMutation, type McpServer } from '@generated/graphql';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';

type DeleteMcpServerModalProps = {
  server: McpServer;
  onDelete?: VoidFunction;
};

export const useDeleteMcpServerModal = ({ server, onDelete }: DeleteMcpServerModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [deleteMcpServerMutation] = useDeleteMcpServerMutation();

  const deleteMcpServer = createAsyncNotification(
    'MCP server deleted successfully.',
    async () => {
      posthog.capture('agent-studio:user_delete-mcp-server_submitted', {
        mcpServerId: server.mcpServerId,
      });
      await deleteMcpServerMutation({
        variables: { mcpServerId: server.mcpServerId },
        update(cache) {
          cache.evict({ id: cache.identify(server) });
          cache.gc();
        },
        onCompleted: () => {
          posthog.capture('agent-studio:user_delete-mcp-server_completed', {
            mcpServerId: server.mcpServerId,
          });
          onDelete?.();
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_delete-mcp-server_failed', {
            mcpServerId: server.mcpServerId,
            error: (error as Error)?.message || 'An error occurred during deletion',
          });
        },
      });
    },
    {
      loadingMessage: 'Deleting MCP server...',
    },
  );

  const showModal = () =>
    showConfirmation({
      onAffirm: deleteMcpServer,
      message: `Are you sure you want to delete ${server?.name || 'this MCP server'}?`,
    });

  return { showModal };
};
